import React, { useEffect, useMemo, useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Visibility,
  Close,
  Download,
  Star,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import api from "../../services/api";
import PermissionGate from "../../components/common/PermissionGate";
import { getTeacherClasses } from "../../services/teacherService";
import { toast } from "react-toastify";
import { downloadBlob } from "../../utils/download";

// Cache last fetched exams so data shows instantly when reopening the page
let examListCache = { exams: [], totalExams: 0 };

const STATUS_FILTERS = ["", "upcoming", "completed", "graded"];

const normalizeTeacherClasses = (p) => {
  const d = p?.data ?? p;
  const l =
    d?.data?.classData ||
    d?.classData ||
    d?.data?.rows ||
    d?.rows ||
    d?.data ||
    d ||
    [];
  return Array.isArray(l)
    ? l.map((c) => ({
        id: c.id ?? c.classId,
        name: c.name || c.className || `Class ${c.id ?? c.classId}`,
        section: c.section ?? c.classSection ?? c.sectionName ?? "",
      }))
    : [];
};

const parseTimeToMinutes = (time) => {
  if (!time || typeof time !== "string") return null;
  const [hoursStr = "", minutesStr = "", secondsStr = ""] = time.split(":");
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  const seconds = Number(secondsStr) || 0;
  if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
    return null;
  }
  return hours * 60 + minutes + seconds / 60;
};

const formatDurationFromMinutes = (minutes) => {
  if (minutes === null || minutes === undefined) return null;
  const rounded = Math.max(0, Math.round(minutes));
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return hours === 1 ? "1 hour" : `${hours} hours`;
  return `${mins} mins`;
};

const getDurationFromTimes = (start, end) => {
  const startMinutes = parseTimeToMinutes(start);
  const endMinutes = parseTimeToMinutes(end);
  if (startMinutes === null || endMinutes === null) return null;
  let diff = endMinutes - startMinutes;
  if (diff < 0) diff += 24 * 60;
  if (diff <= 0) return null;
  return formatDurationFromMinutes(diff);
};

const formatExamTypeLabel = (value) => {
  if (!value && value !== 0) return "";
  const raw = typeof value === "string" ? value : String(value);
  const withSpaces = raw
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  return withSpaces
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const normalizeExamRow = (row) => {
  const safeNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") return fallback;
    const num = Number(value);
    return Number.isNaN(num) ? fallback : num;
  };

  if (!row || typeof row !== "object") {
    return {
      id: `exam-${Math.random().toString(36).slice(2, 9)}`,
      title: "Untitled Exam",
      subject: "",
      class: "",
      date: "",
      totalMarks: 0,
      duration: "",
      status: "upcoming",
      graded: 0,
      total: 0,
    };
  }

  const subjectName =
    row.Subject?.name ?? row.subject?.name ?? row.subjectName ?? row.subject ?? "";
  const className = row.Class?.name ?? row.class?.name ?? row.className ?? row.class ?? "";
  const classSection =
    row.Class?.section ??
    row.class?.section ??
    row.Class?.classSection ??
    row.class?.classSection ??
    row.Class?.sectionName ??
    row.class?.sectionName ??
    "";
  const classDisplay = classSection ? `${className} - ${classSection}` : className;

  let dateValue = row.examDate ?? row.date ?? row.scheduledAt ?? "";
  if (typeof dateValue === "string" && dateValue.includes("T")) {
    dateValue = dateValue.split("T")[0];
  }

  const startTime = row.startTime ?? row.start ?? row.start_time ?? "";
  const endTime = row.endTime ?? row.end ?? row.end_time ?? "";
  const computedDuration = getDurationFromTimes(startTime, endTime);
  let durationValue =
    computedDuration ??
    row.duration ??
    row.examDuration ??
    row.durationLabel ??
    row.durationText ??
    "";
  if (typeof durationValue === "number") {
    durationValue = `${durationValue} mins`;
  }

  const totalMarks = safeNumber(
    row.totalMarks ?? row.marks ?? row.maxMarks ?? row.maximumMarks ?? row.total_marks,
    0
  );

  const passingMarks = safeNumber(
    row.passingMarks ??
      row.passMarks ??
      row.minimumMarks ??
      row.minMarks ??
      row.passing_marks,
    0
  );

  const gradedCount = safeNumber(
    row.gradedCount ??
      row.markedCount ??
      row.completedCount ??
      row.marked ??
      row.graded ??
      row.submittedCount,
    0
  );

  const totalCount = safeNumber(
    row.total ??
      row.totalStudents ??
      row.studentsCount ??
      row.enrolledCount ??
      row.totalCount ??
      row.totalStudent ??
      row.totalAssignments,
    gradedCount
  );

  let statusValue = row.status ?? row.examStatus ?? "";
  if (typeof statusValue === "string") {
    statusValue = statusValue.toLowerCase();
  } else if (row.isCompleted) {
    statusValue = "completed";
  } else {
    statusValue = "";
  }

  const titleValue = row.title ?? row.name ?? row.examName ?? "Untitled Exam";
  const fallbackId = `${titleValue}-${dateValue || Math.random().toString(36).slice(2, 9)}`;
  const id =
    row.id ??
    row.examId ??
    row.exam_id ??
    row._id ??
    row.uuid ??
    row.ID ??
    fallbackId;

  const normalizedTotal = totalCount || gradedCount;
  const rawExamType =
    row.examType ??
    row.type ??
    row.exam_type ??
    row.examCategory ??
    row.examtype ??
    "";
  const examType = formatExamTypeLabel(rawExamType);

  return {
    ...row,
    id,
    title: titleValue,
    subject: subjectName,
    class: classDisplay,
    date: dateValue || "",
    totalMarks,
    duration: durationValue || "",
    status: statusValue || "upcoming",
    graded: gradedCount,
    total: normalizedTotal,
    examType,
    passingMarks,
    examTypeRaw: rawExamType,
  };
};

const TeacherExams = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [downloadingExamId, setDownloadingExamId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    subject: "",
    class: "",
    date: null,
    totalMarks: "",
    duration: "",
  });
  const [exams, setExams] = useState(() => (examListCache.exams?.length ? [...examListCache.exams] : []));
  const [examsLoading, setExamsLoading] = useState(() => !examListCache.exams?.length);
  const [examsError, setExamsError] = useState(null);
  const [totalExams, setTotalExams] = useState(() => examListCache.totalExams || 0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [examsRefreshKey, setExamsRefreshKey] = useState(0);

  const [teacherClasses, setTeacherClasses] = useState([]);
  const [selectedClassName, setSelectedClassName] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const [gradeStudents, setGradeStudents] = useState([]);
  const [gradeMarks, setGradeMarks] = useState({});
  const [gradeStudentsLoading, setGradeStudentsLoading] = useState(false);
  const [gradeStudentsError, setGradeStudentsError] = useState(null);
  const [savingGrades, setSavingGrades] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getTeacherClasses()
      .then((res) => {
        if (cancelled) return;
        setTeacherClasses(normalizeTeacherClasses(res));
      })
      .catch(() => {
        if (!cancelled) setTeacherClasses([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const classNameOptions = useMemo(() => {
    const set = new Set();
    teacherClasses.forEach((c) => {
      const n = String(c?.name || "").trim();
      if (n) set.add(n);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [teacherClasses]);

  const sectionOptions = useMemo(() => {
    if (!selectedClassName) return [];
    const set = new Set();
    teacherClasses
      .filter((c) => String(c?.name || "").trim() === selectedClassName)
      .forEach((c) => {
        const s = String(c?.section || "").trim();
        if (s) set.add(s);
      });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [teacherClasses, selectedClassName]);

  const selectedClassId = useMemo(() => {
    if (!selectedClassName) return null;
    const match = teacherClasses.find(
      (c) =>
        String(c?.name || "").trim() === selectedClassName &&
        (selectedSection ? String(c?.section || "").trim() === selectedSection : true),
    );
    const id = match?.id;
    return id != null && id !== "" ? Number(id) : null;
  }, [teacherClasses, selectedClassName, selectedSection]);

  const queryParams = useMemo(() => {
    const base = {
      search: "",
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      sort: "",
      order: "DESC",
      status: STATUS_FILTERS[activeTab] ?? "",
    };
    if (selectedClassId) base.classId = selectedClassId;
    if (selectedSection) base.section = selectedSection;
    return base;
  }, [activeTab, paginationModel.page, paginationModel.pageSize, selectedClassId, selectedSection]);

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  }, [activeTab, selectedClassName, selectedSection]);

  useEffect(() => {
    let cancelled = false;
    setExamsLoading(true);
    setExamsError(null);
    api
      .get("/exam/all", { params: queryParams })
      .then((res) => {
        if (cancelled) return;
        const raw =
          res?.data?.data?.exams ??
          res?.data?.exams ??
          res?.data?.data ??
          res?.data?.list ??
          res?.data ??
          [];
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list.map((item) => normalizeExamRow(item));
        const pagination =
          res?.data?.data?.pagination ??
          res?.data?.pagination ??
          res?.data?.meta ??
          res?.pagination ??
          res?.meta ??
          {};
        const total =
          pagination.total ??
          pagination.totalItems ??
          pagination.total_count ??
          res?.data?.total ??
          normalized.length;
        const totalExamsVal = typeof total === "number" ? total : normalized.length;
        setExams(normalized);
        setTotalExams(totalExamsVal);
        examListCache = { exams: normalized, totalExams: totalExamsVal };
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.message ??
          err?.response?.data?.error ??
          err?.message ??
          "Failed to load exams";
        setExamsError(message);
        setExams([]);
        setTotalExams(0);
      })
      .finally(() => {
        if (!cancelled) {
          setExamsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [queryParams, examsRefreshKey]);

  useEffect(() => {
    if (!gradeDialogOpen || !selectedExam) return;
    const examId = Number(selectedExam.id);
    if (!examId || Number.isNaN(examId)) {
      setGradeStudentsError("Invalid exam");
      return;
    }
    let cancelled = false;
    setGradeStudentsLoading(true);
    setGradeStudentsError(null);
    api
      .get(`/exam/${examId}/students`)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        const list = Array.isArray(payload?.students) ? payload.students : [];
        setGradeStudents(list);
        const next = {};
        list.forEach((row) => {
          // Always key by numeric DB id — display studentId (e.g. VU26S0002) is not the FK.
          const sid = row.id;
          if (sid == null) return;
          const m = row.marks ?? row.marksObtained;
          next[sid] =
            m === null || m === undefined || m === ""
              ? ""
              : String(m);
        });
        setGradeMarks(next);
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.message ??
          err?.response?.data?.error ??
          err?.message ??
          "Failed to load students";
        setGradeStudentsError(message);
        setGradeStudents([]);
        setGradeMarks({});
      })
      .finally(() => {
        if (!cancelled) setGradeStudentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gradeDialogOpen, selectedExam]);

  const handleCloseGradeDialog = () => {
    setGradeDialogOpen(false);
    setGradeStudents([]);
    setGradeMarks({});
    setGradeStudentsError(null);
    setGradeStudentsLoading(false);
  };

  /** Keep only digits and clamp to [0, max] so values like 42334 cannot remain when max is 100. */
  const sanitizeMarksDigits = (value, max) => {
    if (value === "") return "";
    const digitsOnly = String(value).replaceAll(/\D/g, "");
    if (digitsOnly === "") return "";
    let n = Number.parseInt(digitsOnly, 10);
    if (Number.isNaN(n)) return "";
    if (n < 0) n = 0;
    if (n > max) n = max;
    return String(n);
  };

  const handleSaveGrades = async () => {
    if (!selectedExam) return;
    const examId = Number(selectedExam.id);
    if (!examId || Number.isNaN(examId)) return;
    const maxMarks = Number(selectedExam.totalMarks) || 100;
    const scores = [];
    for (const row of gradeStudents) {
      const sid = row.id; // numeric Student PK expected by POST /exam/:id/scores
      if (sid == null) continue;
      const raw = gradeMarks[sid];
      if (raw === "" || raw === undefined || raw === null) {
        scores.push({ studentId: sid, marks: null });
        continue;
      }
      const n = Number.parseInt(String(raw), 10);
      if (Number.isNaN(n) || n < 0 || n > maxMarks) {
        setGradeStudentsError(
          `Marks must be between 0 and ${maxMarks} for each student.`
        );
        return;
      }
      scores.push({ studentId: sid, marks: n });
    }
    setSavingGrades(true);
    setGradeStudentsError(null);
    try {
      await api.post(`/exam/${examId}/scores`, { scores });
      setExamsRefreshKey((k) => k + 1);
      handleCloseGradeDialog();
    } catch (err) {
      const message =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        err?.message ??
        "Failed to save grades";
      setGradeStudentsError(message);
    } finally {
      setSavingGrades(false);
    }
  };

  const maxMarksForGrade = Number(selectedExam?.totalMarks) || 100;

  const handleDownloadExamReport = async (row) => {
    const examId = Number(row?.id);
    if (!examId || Number.isNaN(examId) || Number(downloadingExamId) === examId) return;
    try {
      setDownloadingExamId(examId);
      const res = await api.get(`/exam/${examId}/report`, { responseType: "blob" });
      downloadBlob(res.data, `exam-${examId}-marks.csv`, "text/csv");
    } catch (err) {
      const message = err?.response?.data?.message ?? err?.message ?? "Failed to download report";
      toast.error(message);
    } finally {
      setDownloadingExamId(null);
    }
  };

  const columns = [
    { field: "title", headerName: "Exam", flex: 1, minWidth: 180 },
    { field: "subject", headerName: "Subject", width: 140 },
    { field: "class", headerName: "Class", width: 120 },
    { field: "examType", headerName: "Exam Type", width: 120 },
    { field: "date", headerName: "Date", width: 110 },
    {
      field: "passingMarks",
      headerName: "Passing Marks",
      width: 115,
      valueFormatter: (params) =>
        params.value === null || params.value === undefined || params.value === ""
          ? "-"
          : `${params.value}`,
    },
    { field: "totalMarks", headerName: "Total Marks", width: 100 },
    { field: "duration", headerName: "Duration", width: 100 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === "graded"
              ? "success"
              : params.value === "completed"
              ? "warning"
              : "info"
          }
        />
      ),
    },
    {
      field: "graded",
      headerName: "Graded",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={`${params.value}/${params.row.total}`}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedExam(params.row);
              setDetailsDialogOpen(true);
            }}
            title="View details"
          >
            <Visibility />
          </IconButton>
          {["completed", "marking", "graded"].includes(params.row.status) && (
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setSelectedExam(params.row);
                setGradeDialogOpen(true);
              }}
              title="Grade students"
            >
              <Star />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => handleDownloadExamReport(params.row)}
            title="Download report"
          >
            {Number(downloadingExamId) === Number(params.row.id) ? <CircularProgress size={18} /> : <Download />}
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Examinations
          </Typography>
          <Typography variant="body2" color="text.secondary">
             View Exams and grade students
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Class</InputLabel>
                <Select
                  value={selectedClassName}
                  label="Class"
                  onChange={(e) => {
                    setSelectedClassName(e.target.value);
                    setSelectedSection("");
                  }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {classNameOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={!selectedClassName}>
                <InputLabel>Division / Section</InputLabel>
                <Select
                  value={selectedSection}
                  label="Division / Section"
                  onChange={(e) => setSelectedSection(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {sectionOptions.map((sec) => (
                    <MenuItem key={sec} value={sec}>
                      {sec}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 2 }}
          >
            <Tab label="All Exams" />
            <Tab label="Upcoming" />
            <Tab label="Completed" />
            <Tab label="Graded" />
          </Tabs>
          {examsError && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="error">
                {examsError}
              </Typography>
            </Box>
          )}
          <DataGrid
            rows={exams}
            columns={columns}
            loading={examsLoading}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            rowCount={totalExams}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>

      {/* Create Exam Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setCreateDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Create Exam
            <IconButton onClick={() => setCreateDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Exam Title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={formData.subject}
                  label="Subject"
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                >
                  <MenuItem value="Mathematics">Mathematics</MenuItem>
                  <MenuItem value="Physics">Physics</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  value={formData.class}
                  label="Class"
                  onChange={(e) =>
                    setFormData({ ...formData, class: e.target.value })
                  }
                >
                  <MenuItem value="10A">10A</MenuItem>
                  <MenuItem value="10B">10B</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label="Exam Date"
                value={formData.date}
                onChange={(date) => setFormData({ ...formData, date: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Total Marks"
                type="number"
                value={formData.totalMarks}
                onChange={(e) =>
                  setFormData({ ...formData, totalMarks: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Duration"
                value={formData.duration}
                onChange={(e) =>
                  setFormData({ ...formData, duration: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* View Exam Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setDetailsDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            Exam Details
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedExam && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Title</Typography>
                <Typography variant="body1" fontWeight={500}>{selectedExam.title}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Subject</Typography>
                <Typography variant="body1">{selectedExam.subject || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Class</Typography>
                <Typography variant="body1">{selectedExam.class || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Date</Typography>
                <Typography variant="body1">{selectedExam.date || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body1">{selectedExam.duration || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Marks</Typography>
                <Typography variant="body1">{selectedExam.totalMarks ?? "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Passing Marks</Typography>
                <Typography variant="body1">{selectedExam.passingMarks ?? "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Exam Type</Typography>
                <Typography variant="body1">{selectedExam.examType || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip label={selectedExam.status || "—"} size="small" sx={{ textTransform: "capitalize" }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Graded</Typography>
                <Typography variant="body1">{selectedExam.graded ?? 0} / {selectedExam.total ?? 0} students</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Grade Students Dialog */}
      <Dialog
        open={gradeDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; handleCloseGradeDialog(); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Grade Students - {selectedExam?.title}
            <IconButton onClick={handleCloseGradeDialog} aria-label="close">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {gradeStudentsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGradeStudentsError(null)}>
              {gradeStudentsError}
            </Alert>
          )}
          {gradeStudentsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Roll No</TableCell>
                    <TableCell>Student Name</TableCell>
                    <TableCell>
                      Marks (out of {maxMarksForGrade})
                    </TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gradeStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No students enrolled for this exam.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    gradeStudents.map((student) => {
                      const sid = student.id;
                      const raw = gradeMarks[sid];
                      const markNum =
                        raw === "" || raw === undefined || raw === null
                          ? null
                          : Number.parseInt(String(raw), 10);
                      const markValid =
                        markNum !== null &&
                        !Number.isNaN(markNum) &&
                        markNum >= 0 &&
                        markNum <= maxMarksForGrade;
                      const hasMark = markValid;
                      const displayName = student.name || "Unknown";
                      const initial =
                        displayName.trim().charAt(0).toUpperCase() || "?";
                      return (
                        <TableRow key={sid}>
                          <TableCell>{student.rollNo}</TableCell>
                          <TableCell>
                            <Box
                              sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                              <Avatar sx={{ width: 28, height: 28 }}>
                                {initial}
                              </Avatar>
                              {displayName}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              value={raw ?? ""}
                              placeholder={`0–${maxMarksForGrade}`}
                              onChange={(e) => {
                                const next = sanitizeMarksDigits(
                                  e.target.value,
                                  maxMarksForGrade
                                );
                                setGradeMarks((prev) => ({
                                  ...prev,
                                  [sid]: next,
                                }));
                              }}
                              sx={{ width: 140 }}
                              disabled={savingGrades}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={hasMark ? "Graded" : "Pending"}
                              size="small"
                              color={hasMark ? "success" : "warning"}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGradeDialog} disabled={savingGrades}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveGrades}
            disabled={
              savingGrades ||
              gradeStudentsLoading ||
              gradeStudents.length === 0
            }
          >
            {savingGrades ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Save Grades"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherExams;
